import { useCallback, useRef } from "react";

import { Flex, Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { routes } from "config/routes";
import {
  PrecountEquipmentList,
  PrecountEquipmentInfo,
} from "flows/Activities/components/TransportEquipment/PrecountEquipmentList";
import { useSaveTransportEquipmentCountMutation } from "flows/Activities/queries/deliveryCheckin/transportEquipments.generated";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import { Page } from "shared/components/Page";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Button } from "ui/Button/Button";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";

export function TransportEquipmentPrecountPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();

  const { externalSupplierId } = useDeliveryStore(
    useShallow((state) => ({
      externalSupplierId: state.externalSupplierId,
    })),
  );

  const equipmentCountInfoRef = useRef<PrecountEquipmentInfo>({ lineItems: [] });

  const handleCountsChanged = useCallback((info: PrecountEquipmentInfo) => {
    equipmentCountInfoRef.current = info;
  }, []);

  const [saveTransportEquipmentCount, { loading }] = useSaveTransportEquipmentCountMutation({
    onCompleted: () => {
      navigate(routes.activities.root);
      showToastUI({
        variant: "success",
        title: "components.activities.transport-equipment.precount-page.submit.success.title",
      });
    },
    onError: () => {
      showToastUI({
        title: "components.activities.transport-equipment.precount-page.submit.error.title",
      });
    },
  });

  const handleSubmit = () => {
    const { lineItems } = equipmentCountInfoRef.current;
    saveTransportEquipmentCount({
      variables: {
        input: {
          externalSupplierId: externalSupplierId!,
          lineItems: lineItems.map(({ equipmentTypeId, preparing }) => ({
            equipmentTypeId,
            returned: preparing,
            received: 0,
          })),
        },
      },
    });
  };

  return (
    <Page bg="grey.100" isCentered isFull>
      <NavigationHeader
        title={intl.formatMessage({
          id: "components.activities.transport-equipment.precount-page.header-title",
        })}
        onClickGoBack={() => navigate(-1)}
      />
      <Flex
        h="full"
        w="full"
        px="s200"
        py="s100"
        direction="column"
        overflow="auto"
        alignContent="flex-start"
      >
        <PrecountEquipmentList onCountsChanged={handleCountsChanged} />
      </Flex>
      <Box bg="white" p="s200" w="full">
        <Button variant="primary" isFullWidth onClick={handleSubmit} isLoading={loading}>
          {intl.formatMessage({
            id: "components.activities.transport-equipment.precount-page.cta",
          })}
        </Button>
      </Box>
    </Page>
  );
}
